import { Service } from 'typedi';
import { action, makeObservable, observable } from 'mobx';
import { DataSourceAccountService } from '../../services/DataSourceAccountService';
import { AccountEntity } from '../../core/entity/AccountEntity';
import { OrganizationEntity } from '../../core/entity/OrganizationEntity';
import { OrganizationsService } from '../../services/OrganizationsService';
import { SiteEntity } from '../../core/entity/SiteEntity';
import { Notifications } from '../Notifications';
import { FeatureEntity, FeatureService } from '../../services/FeatureService';
import i18n from '../../i18n';
import { AnalyticsAccessService } from '../../services/AnalyticsAccessService';
import { AnalyticsAccessEntity } from '../../core/entity/AnalyticsAccessEntity';

@Service()
export default class OrganizationsPageStore {
    @observable isLoaded: boolean = false;

    @observable organizations: OrganizationEntity[] = [];

    @observable organization?: OrganizationEntity;
    @observable accounts: AccountEntity[];
    /** Full `analytics_access` catalog (pricing tiers + feature access definitions). */
    @observable analyticsAccessCatalog: AnalyticsAccessEntity[];
    /** Current org’s linked analytics access rows (for edit form); not part of `OrganizationEntity`. */
    @observable organizationAnalyticsAccess: AnalyticsAccessEntity[];
    accountApplications: SiteEntity[];
    @observable applications: SiteEntity[];

    @observable features: FeatureEntity[] = [];

    constructor(
        private service: OrganizationsService,
        private accountService: DataSourceAccountService,
        private featureService: FeatureService,
        private analyticsAccessService: AnalyticsAccessService,
    ) {
        makeObservable(this);
        this.accounts = [];
        this.analyticsAccessCatalog = [];
        this.organizationAnalyticsAccess = [];
        this.accountApplications = [];
        this.applications = [];
    }

    async load() {
        await this.fetchOrganizations();
        this.isLoaded = true;
    }

    @action
    async fetchOrganizations(): Promise<OrganizationEntity[]> {
        const organizations = await this.service.getOrganizations();
        this.organizations = organizations.sort((a, b) => a.name.localeCompare(b.name));
        return this.organizations;
    }

    @action
    async fetchOrganization(id: string): Promise<OrganizationEntity | null> {
        try {
            const [org, orgFeatures, orgAccess] = await Promise.all([
                this.service.getOrganizationById(id),
                this.featureService.getOrganizationFeatures(id),
                this.analyticsAccessService.getOrganizationAccess(id),
                this.fetchApplicationsIfNeeded(id),
            ]);
            this.organization = org;
            this.features = orgFeatures;
            this.organizationAnalyticsAccess = orgAccess;
            this.updateAccountApplications(org.all_sites);
        } catch (e) {
            Notifications.fromException(e);
            throw e;
        }

        return this.organization;
    }

    private async fetchApplicationsIfNeeded(id: string) {
        if (this.applications.length === 0) {
            try {
                this.applications = await this.service.getApplications(id);
            } catch (e) {
                Notifications.fromException(e);
            }
        }
    }

    @action async saveFeatures(org: OrganizationEntity | string, features: FeatureEntity[]) {
        const res = await this.featureService.setOrganizationFeatures(features, org);
        if (res) {
            Notifications.success(i18n.t('common:saveOrgFeaturesSuccess', 'Organization Features successfully saved'));
            this.features = features;
        }

        return res;
    }

    @action
    async saveOrganizationAnalyticsAccess(orgId: string, analyticsAccessIds: number[]) {
        const updated = await this.analyticsAccessService.setOrganizationAccess(orgId, analyticsAccessIds);
        if (this.organization?.id === orgId) {
            Notifications.success(i18n.t('common:saveOrgAnalyticsAccessSuccess', 'Organization Analytics Access successfully saved '));
            this.organizationAnalyticsAccess = updated;
        }
        return updated;
    }

    @action
    async fetchAccounts() {
        if (!this.accounts?.length) {
            this.accounts = await this.accountService.getAccounts();
        }
    }

    @action
    async fetchAnalyticsAccess() {
        if (!this.analyticsAccessCatalog?.length) {
            this.analyticsAccessCatalog = await this.analyticsAccessService.getAll();
        }
    }

    @action
    async updateOrgagnizationPractice(site: SiteEntity) {
        return await this.service.updateOrganizationPractice(site);
    }

    @action
    async createOrganization(): Promise<OrganizationEntity> {
        this.organization = new OrganizationEntity();
        this.features = [];
        this.organizationAnalyticsAccess = [];
        return this.organization;
    }

    @action
    async save(entity: OrganizationEntity): Promise<OrganizationEntity> {
        this.organization = await this.service.saveOrganization(entity);
        return this.organization as OrganizationEntity;
    }

    @action
    async saveOrg(org: OrganizationEntity, linkAccounts?: AccountEntity[], unlinkAccounts?: AccountEntity[]): Promise<OrganizationEntity> {
        if (!org.id) {
            const { id } = await this.save(org);
            org.id = id;
        }

        if (linkAccounts?.length) {
            const linkAccountIds = linkAccounts.map(({ id }) => id);
            const { all_sites } = await this.linkAccounts(org.id, linkAccountIds);
            org.all_sites = this.mergeOrgSitesFromLinkResponse(all_sites, org.all_sites);
        }

        if (unlinkAccounts?.length) {
            const unlinkAccountIds = unlinkAccounts.map(({ id }) => id);
            const { all_sites } = await this.unlinkAccounts(org.id, unlinkAccountIds);
            org.all_sites = this.mergeOrgSitesFromLinkResponse(all_sites, org.all_sites);
        }

        const res = await this.save(org);
        Notifications.success(i18n.t('common:saveOrganizationSuccess', 'Organization successfully saved'));
        return res;
    }

    private mergeOrgSitesFromLinkResponse(target: SiteEntity[], source: SiteEntity[]) {
        target.forEach(targetSite => {
            const sourceSite = source.find(({ siteId, id }) => String(targetSite.siteId) === String(siteId || id));
            if (!sourceSite) {
                return;
            }

            targetSite.isEnabled = sourceSite.isEnabled;
            targetSite.idexxPracticeName = sourceSite.idexxPracticeName;
            targetSite.idexxSapId = sourceSite.idexxSapId;
            targetSite.idexxZip = sourceSite.idexxZip;
        });

        return target;
    }

    @action
    async setMasterApplication(orgId: string, applicationId: string): Promise<OrganizationEntity | null> {
        try {
            await this.service.setMasterApplication(orgId, applicationId);
        } catch (e) {
            Notifications.fromException(e);
            throw e;
        }
        Notifications.success(i18n.t('common:masterApplicationIsSet'));
        return this.fetchOrganization(orgId);
    }

    @action
    async fetchAccountApplications(accountId: string) {
        const accountSites = this.accountApplications.filter(site => site.accountId === accountId);
        if (!accountSites.length) {
            try {
                const sites = await this.service.getAccountApplications(accountId);
                this.updateAccountApplications(sites);
            } catch (e) {
                Notifications.fromException(e);
                throw e;
            }
        }
    }

    private updateAccountApplications(sites: SiteEntity[]) {
        const siteAccountIds = sites.map(({ accountId }) => accountId);
        const notOrgSites = this.accountApplications.filter(({ accountId }) => !siteAccountIds.includes(accountId));
        const accountApplications = [...notOrgSites, ...sites];
        accountApplications.sort((a, b) => Number(a.id) - Number(b.id));
        this.accountApplications = accountApplications;
    }

    @action
    async linkAccounts(orgId: string, accountIds: string[]): Promise<OrganizationEntity> {
        try {
            this.organization = await this.service.linkAccounts(orgId, accountIds);
            Notifications.success(i18n.t('common:linkAccountsSuccess'));
            return this.organization;
        } catch (e) {
            Notifications.fromException(e);
            throw e;
        }
    }

    @action
    async unlinkAccounts(orgId: string, accountIds: string[]): Promise<OrganizationEntity> {
        try {
            this.organization = await this.service.unlinkAccounts(orgId, accountIds);
            Notifications.success(i18n.t('common:unlinkAccountsSuccess'));
            return this.organization;
        } catch (e) {
            Notifications.fromException(e);
            throw e;
        }
    }

    @action dispose() {
        this.organizations = [];
        this.accounts = [];
        this.accountApplications = [];
        this.applications = [];
        this.analyticsAccessCatalog = [];
        this.organizationAnalyticsAccess = [];
    }

    @action
    async delete(organizationId: string) {
        try {
            const response = await this.service.deleteOrganization(organizationId);
            Notifications.success(i18n.t('common:deleteOrgSuccess'));
            return response;
        } catch (e) {
            Notifications.fromException(e);
            throw e;
        }
    }
}
